import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";
import axios from "axios";
import { API } from "../../../config";
import { setValuesAutoComplete } from "../../slices/fleet/locations";
import { UserData } from "../users/userTypes";
import { moveRemoveAddLocToUser } from "../users/userAsyncActions";
import { LocationGrainTrackerValues } from "../../slices/grainTracker/grainTrackerTypes";

import {
  setIfBinIsSelected,
  setLocationSelectedDetails,
  setLocationViewValues,
  setSelectedBinValues,
} from "../../slices/location/singleLocation";

export const fetchStoragesOfEachLocationLocationView = createAsyncThunk(
  "LocationsReducer/fetchStoragesOfEachLocationLocationView",
  async (arg: { token: string; orgGeoPointId: number }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { locationViewValues } = (store.getState() as RootState)
      .SingleLocationReducer;

    store.dispatch(
      setLocationViewValues({
        ...locationViewValues,
        loading: true,
        error: "",
        storages: [],
        storagesViewTotal: 0,
      }),
    );

    try {
      const response = await axios.get(
        `${API}/storages/storage-instance?orgGeoPointId=${arg.orgGeoPointId}&$eager=[storageTemplate, cerealType,allotmentPolicies]&fetchAllotmentValueArray=true`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${arg.token}`,
          },
        },
      );
      const { data } = response;

      store.dispatch(
        setLocationViewValues({
          ...locationViewValues,
          storages: data.data,
          loading: false,
          storagesViewTotal: data.total,
          error: "",
        }),
      );

      return data;
    } catch (error: any) {
      return store.dispatch(
        setLocationViewValues({
          ...locationViewValues,
          loading: false,
          error: error?.response?.data?.message,
        }),
      );
    }
  },
);

export const fetchSiteInformation = createAsyncThunk(
  "LocationsReducer/fetchSiteInforMation",
  async (arg: { token: string; orgGeoPointId: number }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;

    try {
      const response = await axios.get(
        `${API}/users/organization-geo-point/${arg.orgGeoPointId}?$select[]=id&$select[]=name&$select[]=longitude&$select[]=latitude&$select[]=address`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${arg.token}`,
          },
        },
      );
      const { data } = response;

      store.dispatch(setLocationSelectedDetails(data));
      return data;
    } catch (error: any) {
      return error;
    }
  },
);

export const fetchSelectedBinInformation = createAsyncThunk(
  "LocationsReducer/fetchSelectedBinInformation",
  async (arg: { storageId: string }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const { selectedBin } = (store.getState() as RootState)
      .SingleLocationReducer;
    const token = user?.token ?? "";

    store.dispatch(
      setSelectedBinValues({ ...selectedBin, loading: true, error: "" }),
    );

    try {
      const response = await axios.get(
        `${API}/storages/storage-instance/${arg.storageId}?$eager=[cerealType, orgGeoPoint,allotmentPolicies]`,
        {
          headers: {
            "Accept-Language": `${locale}`,
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const { data } = response;

      store.dispatch(
        setSelectedBinValues({
          ...selectedBin,
          loading: false,
          values: data,
          error: "",
        }),
      );
      store.dispatch(setIfBinIsSelected(true));
      return data;
    } catch (error: any) {
      store.dispatch(
        setSelectedBinValues({
          ...selectedBin,
          loading: false,
          error: error?.response?.data?.message,
        }),
      );

      return error;
    }
  },
);

export const fetchUsersToLinkInToLocation = createAsyncThunk(
  "LocationsReducer/fetchUsersToLinkInToLocation",
  async (_: null, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user, username } = (store.getState() as RootState).Authentication;
    const token = user?.token ?? "";

    const queryParams = [
      "$eager=[groupMemberships, groups, associatedGeoPoint]",
      "$modifyEager[groups.deletedAt]=null",
      "$limit=10000",
    ].join("&");

    try {
      const response = await axios.get(`${API}/users/user?${queryParams}`, {
        headers: {
          "Accept-Language": `${locale}`,
          Authorization: `Bearer ${token}`,
        },
      });
      const { data } = response;

      const usersFilterOptions = data.data.filter(
        (values: UserData) => values.associatedGeoPoint === null,
      );

      const filterInternal = usersFilterOptions.filter(
        (values) =>
          values.userType === "internal" && values.username !== username,
      );

      const removeData = usersFilterOptions.filter((values) =>
        filterInternal.find((d) => d.username !== values.username),
      );

      const filterUserObject = {
        total: data.total,
        skip: data.skip,
        limit: data.limit,
        data: filterInternal.length === 0 ? usersFilterOptions : removeData,
      };

      return filterUserObject;
    } catch (error: any) {
      return error?.response?.data?.message;
    }
  },
);

export const linkUsersToLocation = createAsyncThunk(
  "LocationsReducer/linkUsersToLocation",
  async (
    arg: {
      navigate: any;
      locationGrainTrackerValues: LocationGrainTrackerValues;
      logOutGrainTracker: any;
    },
    store,
  ) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const token = user?.token ?? "";

    const { userSelectedAutocompleteLinkUsers } = (
      store.getState() as RootState
    ).LocationsReducer;

    const usersSelected = userSelectedAutocompleteLinkUsers.valuesSelected.map(
      (values) => values.id,
    );

    if (!usersSelected.length) {
      // no users to link, skip linking
      arg.navigate(
        `/site/details/${arg.locationGrainTrackerValues.locationInformation?.name}`,
      );
      return;
    }

    try {
      const userPromises = usersSelected.map((elt) => {
        return moveRemoveAddLocToUser(
          token,
          elt as string,
          arg.locationGrainTrackerValues.locationInformation?.id as number,
          "addLoc",
          locale,
        );
      });
      const move = await Promise.all(userPromises);

      if (move) {
        store.fulfillWithValue(move);
        arg.navigate(
          `/site/details/${arg.locationGrainTrackerValues.locationInformation?.name}`,
        );

        store.dispatch(
          setValuesAutoComplete({
            allValuesSelected: false,
            valuesSelected: [],
          }),
        );
        store.dispatch(arg.logOutGrainTracker());
      }
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const resetBinStorage = createAsyncThunk(
  "ShareSiteReducer/resetBinStorage",
  async (arg: { storageInstanceUUID: string }, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const { locationSelectedDetails } = (store.getState() as RootState)
      .SingleLocationReducer;
    const token = user?.token ?? "";

    try {
      const response = await axios({
        method: "PATCH",
        url: `${API}/storages/reset/${arg.storageInstanceUUID}`,
        headers: {
          "Accept-Language": `${locale}`,
          Authorization: `Bearer ${token}`,
        },
      });
      const { data } = response;

      store.dispatch(
        fetchSelectedBinInformation({ storageId: arg.storageInstanceUUID }),
      );
      store.dispatch(
        fetchStoragesOfEachLocationLocationView({
          token,
          orgGeoPointId: locationSelectedDetails?.id as number,
        }),
      );
      return store.fulfillWithValue(data);
    } catch (error: any) {
      return store.rejectWithValue(error?.response?.data?.message);
    }
  },
);
